import { useEffect, useState } from "react";
import {
  Check,
  ChevronDown,
  Loader2,
  Mail,
  ShieldCheck,
  UserMinus,
  UserPlus,
  UserCheck,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const ROLES = [
  { key: "admin", label: "Administrator", hint: "Members, content, exports, analytics" },
  { key: "support", label: "Client Support", hint: "Consultations and basic member info" },
  { key: "marketing", label: "Marketing Manager", hint: "Subscribers and marketing exports" },
  { key: "owner", label: "Owner", hint: "Full control, including this team page" },
];
const ROLE_LABEL = Object.fromEntries(ROLES.map((r) => [r.key, r.label]));

const fmt = (iso) =>
  iso
    ? new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
    : "never";

export default function AdminTeam() {
  const { member: me } = useAuth();
  const [data, setData] = useState(null);
  const [invite, setInvite] = useState({ email: "", name: "", role: "admin" });
  const [inviting, setInviting] = useState(false);
  const [activityFor, setActivityFor] = useState(null);
  const [activity, setActivity] = useState(null);

  const load = () => api.get("/admin/team").then(({ data }) => setData(data));

  useEffect(() => {
    document.title = "Admin Users & Permissions | Rooted Healing Admin";
    load().catch(() => setData({ staff: [], invites: [] }));
  }, []);

  const sendInvite = async (e) => {
    e.preventDefault();
    setInviting(true);
    try {
      await api.post("/admin/team/invite", invite);
      toast.success(`Invitation emailed to ${invite.email}.`);
      setInvite({ email: "", name: "", role: "admin" });
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
    setInviting(false);
  };

  const changeRole = async (userId, role) => {
    try {
      await api.patch(`/admin/team/${userId}`, { role });
      toast.success("Role updated.");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const setStatus = async (userId, status) => {
    try {
      await api.post(`/admin/team/${userId}/status`, { status });
      toast.success(status === "deactivated" ? "Access revoked." : "Access restored.");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const openActivity = async (userId) => {
    if (activityFor === userId) {
      setActivityFor(null);
      setActivity(null);
      return;
    }
    setActivityFor(userId);
    setActivity(null);
    try {
      const { data } = await api.get(`/admin/team/${userId}/activity`);
      setActivity(data);
    } catch {
      toast.error("Could not load activity.");
      setActivityFor(null);
    }
  };

  if (!data) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="h-7 w-7 animate-spin text-forest" />
      </div>
    );
  }

  const pendingIds = new Set(data.invites.map((i) => i.member_id));

  return (
    <div data-testid="admin-team">
      <h1 className="font-serif text-3xl text-forest">Admin Users & Permissions</h1>
      <p className="mt-2 max-w-2xl text-sm leading-relaxed text-taupe">
        Invite trusted people, assign their role, and revoke access anytime. Only the Owner
        can manage this page. Every privileged sign-in requires an emailed security code.
      </p>

      <form
        data-testid="team-invite-form"
        onSubmit={sendInvite}
        className="mt-8 rounded-[1.75rem] border border-forest/10 bg-white/75 p-6"
      >
        <h2 className="font-serif text-xl text-forest">Invite an administrator</h2>
        <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
          <input
            data-testid="invite-email"
            type="email"
            required
            placeholder="Email address"
            value={invite.email}
            onChange={(e) => setInvite((f) => ({ ...f, email: e.target.value }))}
            className="w-full rounded-full border border-forest/20 bg-white/70 px-5 py-3 text-sm outline-none focus:border-gold"
          />
          <input
            data-testid="invite-name-input"
            required
            placeholder="Full name"
            value={invite.name}
            onChange={(e) => setInvite((f) => ({ ...f, name: e.target.value }))}
            className="w-full rounded-full border border-forest/20 bg-white/70 px-5 py-3 text-sm outline-none focus:border-gold"
          />
          <select
            data-testid="invite-role"
            value={invite.role}
            onChange={(e) => setInvite((f) => ({ ...f, role: e.target.value }))}
            className="w-full rounded-full border border-forest/20 bg-white/70 px-5 py-3 text-sm outline-none focus:border-gold"
          >
            {ROLES.filter((r) => r.key !== "owner").map((r) => (
              <option key={r.key} value={r.key}>
                {r.label} · {r.hint}
              </option>
            ))}
            <option value="owner">Owner · Full control</option>
          </select>
        </div>
        <button
          data-testid="invite-send"
          disabled={inviting}
          className="mt-4 inline-flex items-center gap-2 rounded-full bg-forest px-6 py-3 text-sm font-semibold text-ivory transition-colors hover:bg-forest-deep disabled:opacity-50"
        >
          {inviting ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4" />}
          Send Invitation Email
        </button>
      </form>

      <div className="mt-8 rounded-[1.75rem] border border-forest/10 bg-white/75 p-6">
        <h2 className="font-serif text-xl text-forest">Current team</h2>
        <div className="mt-4 flex flex-col gap-3">
          {data.staff.map((s) => {
            const status = s.status || "active";
            const isSelf = s.user_id === me?.user_id;
            const isPending = pendingIds.has(s.user_id) || status === "invited";
            return (
              <div
                key={s.user_id}
                data-testid={`team-member-${s.user_id}`}
                className="rounded-2xl border border-forest/10 p-5"
              >
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <p className="font-semibold text-forest">
                      {s.name}
                      {isSelf && <span className="ml-2 text-xs font-normal text-taupe">(you)</span>}
                    </p>
                    <p className="text-xs text-charcoal/70">{s.email}</p>
                    <p className="mt-1 text-[11px] text-taupe">
                      Last sign-in: {fmt(s.last_login_at)} · Joined {fmt(s.created_at)}
                    </p>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <span
                      data-testid={`team-status-${s.user_id}`}
                      className={`rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-wide ${
                        status === "active"
                          ? "bg-sage text-forest"
                          : status === "invited"
                            ? "bg-gold/15 text-gold-dark"
                            : "bg-charcoal/10 text-taupe"
                      }`}
                    >
                      {isPending ? "invitation pending" : status}
                    </span>
                    <select
                      data-testid={`team-role-${s.user_id}`}
                      value={s.role}
                      disabled={isSelf}
                      onChange={(e) => changeRole(s.user_id, e.target.value)}
                      className="rounded-full border border-forest/20 bg-white/70 px-4 py-2 text-xs font-semibold text-forest outline-none focus:border-gold disabled:opacity-50"
                    >
                      {ROLES.map((r) => (
                        <option key={r.key} value={r.key}>
                          {r.label}
                        </option>
                      ))}
                    </select>
                    <button
                      data-testid={`team-activity-${s.user_id}`}
                      onClick={() => openActivity(s.user_id)}
                      className="inline-flex items-center gap-1.5 rounded-full border border-forest/20 px-4 py-2 text-xs font-semibold text-forest hover:border-gold"
                    >
                      <ChevronDown
                        className={`h-3.5 w-3.5 transition-transform ${activityFor === s.user_id ? "rotate-180" : ""}`}
                      />
                      Activity
                    </button>
                    {!isSelf &&
                      (status === "deactivated" ? (
                        <button
                          data-testid={`team-reactivate-${s.user_id}`}
                          onClick={() => setStatus(s.user_id, "active")}
                          className="inline-flex items-center gap-1.5 rounded-full border border-forest/20 px-4 py-2 text-xs font-semibold text-forest hover:border-gold"
                        >
                          <UserCheck className="h-3.5 w-3.5" />
                          Restore access
                        </button>
                      ) : (
                        <button
                          data-testid={`team-deactivate-${s.user_id}`}
                          onClick={() => setStatus(s.user_id, "deactivated")}
                          className="inline-flex items-center gap-1.5 rounded-full border border-red-900/20 px-4 py-2 text-xs font-semibold text-red-900/70 hover:border-red-900/50"
                        >
                          <UserMinus className="h-3.5 w-3.5" />
                          Revoke access
                        </button>
                      ))}
                  </div>
                </div>
                {activityFor === s.user_id && (
                  <div
                    data-testid={`team-activity-panel-${s.user_id}`}
                    className="mt-4 rounded-2xl bg-sage/50 p-5"
                  >
                    {!activity ? (
                      <Loader2 className="h-5 w-5 animate-spin text-forest" />
                    ) : (
                      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                        <div>
                          <p className="text-xs font-semibold uppercase tracking-[0.15em] text-forest">
                            Sign-in history
                          </p>
                          {activity.logins.length === 0 ? (
                            <p className="mt-2 text-xs text-taupe">No sign-ins recorded yet.</p>
                          ) : (
                            <ul className="mt-2 flex flex-col gap-1.5">
                              {activity.logins.map((l, i) => (
                                <li key={i} className="text-xs text-charcoal/75">
                                  {fmt(l.at)} · via {l.method}
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                        <div>
                          <p className="text-xs font-semibold uppercase tracking-[0.15em] text-forest">
                            Admin actions
                          </p>
                          {activity.actions.length === 0 ? (
                            <p className="mt-2 text-xs text-taupe">No recorded actions yet.</p>
                          ) : (
                            <ul className="mt-2 flex flex-col gap-1.5">
                              {activity.actions.map((a, i) => (
                                <li key={i} className="text-xs text-charcoal/75">
                                  {fmt(a.at)} · {a.action}
                                  {a.target ? ` · ${a.target}` : ""}
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div className="mt-6 rounded-2xl bg-sage/60 px-5 py-4 text-xs leading-relaxed text-charcoal/70">
        <div className="flex items-start gap-2">
          <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-forest" />
          <p>
            Safeguards: only the Owner manages this page; roles are enforced on the server for
            every request; you cannot change your own role or revoke your own access; the final
            active Owner can never be removed. Staff accounts cannot sign in through Google and
            always require the emailed security code.
          </p>
        </div>
      </div>
      <p className="mt-4 flex items-center gap-2 text-xs text-taupe">
        <Mail className="h-3.5 w-3.5" />
        Pending invitations appear above as "invitation pending" until accepted.
        <Check className="hidden" />
      </p>
    </div>
  );
}
